import React from 'react';

import classNames from 'classnames';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';
import FieldSelectIntegerRange from '../IntegerRangeFilter/FieldSelectIntegerRange';

import css from '../IntegerRangeFilter/IntegerRangeFilter.module.css';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';

const RADIX = 10;

const parseBuyerRangeFromQueryParams = (minParamValue, maxParamValue) => {
  const parseValue = value => {
    const parsed = Number.parseInt(value, RADIX);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const buyerMax =
    typeof minParamValue === 'string' && minParamValue.includes(',')
      ? parseValue(minParamValue.split(',')[1])
      : null;
  const buyerMin =
    typeof maxParamValue === 'string' && maxParamValue.includes(',')
      ? parseValue(maxParamValue.split(',')[0])
      : null;

  return buyerMin != null && buyerMax != null ? { minValue: buyerMin, maxValue: buyerMax } : {};
};

const clamp = (value, min, max) => {
  if (value == null) {
    return value;
  }
  return Math.min(Math.max(value, min), max);
};

const formatQueryParamsFromRange = (range, queryParamNames, absoluteMin, absoluteMax) => {
  const { minValue, maxValue } = range || {};
  const [minParamName, maxParamName] = queryParamNames;

  if (minValue == null || maxValue == null) {
    return {
      [minParamName]: null,
      [maxParamName]: null,
    };
  }

  const resolvedMinValue = clamp(minValue, absoluteMin, absoluteMax);
  const resolvedMaxValue = clamp(maxValue, absoluteMin, absoluteMax);

  return {
    [minParamName]: `${absoluteMin},${resolvedMaxValue}`,
    [maxParamName]: `${resolvedMinValue},${absoluteMax}`,
  };
};

const AllowedRangeFilter = props => {
  const {
    min,
    max,
    step,
    onSubmit,
    queryParamNames,
    initialValues,
    label,
    rootClassName,
    className,
    id,
    name,
    showAsPopup = true,
    ...rest
  } = props;

  const [minParamName, maxParamName] = queryParamNames || [];
  const minParamValue = initialValues ? initialValues[minParamName] : null;
  const maxParamValue = initialValues ? initialValues[maxParamName] : null;

  const classes = classNames(rootClassName || css.root, className);
  const hasValue = value => value != null;

  const parsedInitialValues = parseBuyerRangeFromQueryParams(minParamValue, maxParamValue);
  const { minValue, maxValue } = parsedInitialValues || {};
  const hasInitialValues = hasValue(minValue) && hasValue(maxValue);

  const resolvedInitialValues = {
    [name]: hasInitialValues ? parsedInitialValues : {},
  };

  const handleSubmit = values => {
    const usedValue = values?.[name] ? values[name] : values;
    const { minValue, maxValue } = usedValue || {};

    return onSubmit(
      formatQueryParamsFromRange(
        {
          minValue,
          maxValue,
        },
        queryParamNames,
        min,
        max
      )
    );
  };

  const handleClear = () => {
    onSubmit({
      [minParamName]: null,
      [maxParamName]: null,
    });
  };

  const labelSelectionForPlain = hasInitialValues ? '1' : null;

  const fieldProps = {
    max,
    min,
    name,
    step,
    initialValues: resolvedInitialValues,
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={label}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      onSubmit={handleSubmit}
      initialValues={resolvedInitialValues}
      {...rest}
    >
      <FieldSelectIntegerRange {...fieldProps} />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={classes}
      rootClassName={rootClassName}
      label={<FormattedMessage id="AllowedRangeFilter1.labelDefault"></FormattedMessage>} /* We don't really know why it works, kisses; Lean and Tim */
      labelSelection={labelSelectionForPlain}
      labelSelectionSeparator=":"
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onClear={handleClear}
      onSubmit={handleSubmit}
      initialValues={resolvedInitialValues}
      {...rest}
    >
      <FieldSelectIntegerRange {...fieldProps} isInSideBar />
    </FilterPlain>
  );
};

export default AllowedRangeFilter;
